import { useState } from "react";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { downloadFile } from "../common/api/downloadFile";
import { useDotLegalSnackbar, useStateSessionStorage } from "@dotlegal/dotlegal-ui-components";
import { useStateUrlParams, useStateUrlParamsArray } from "../common/hooks/useStateUrlParams";
import { DataControllerRecordModel } from "../DataControllerRecord/DataControllerRecord.types";
import { DataProcessorRecordTableModel } from "../processingActivityRecord/dataProcessorRecord/DataProcessorRecord.types";
import { useTranslation } from "../localization/useTranslation";
import { RecordType } from "./Record.types";
import { usePlanContext } from "../auth/planProvider/PlanProvider";
import {
    filterDataProcessorRecord,
    getDataControllerTranslations,
    getDataProcessorTranslations,
    getExportFileName,
} from "../processingActivityRecord/ProcessingActivityRecord.utils";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

export function useRecordDataMapping() {
    const { translateDate, translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures } = usePlanContext();
    const snackBar = useDotLegalSnackbar();

    const [groupEntityId, setGroupEntityId] = useStateUrlParams("groupEntityId", null);
    const [onlyUserSpecific, setOnlyUserSpecific] = useStateSessionStorage("recordOnlyUserSpecific", !permissions.canAccessAllData);
    const [isExporting, setIsExporting] = useState(false);
    const [processingActivityIds, setProcessingActivityIds] = useStateUrlParamsArray("processingActivityIds", []);
    const [processingAreaIds, setProcessingAreaIds] = useStateUrlParamsArray("processingAreaIds", []);
    const [searchString, setSearchString] = useState("");
    const [selectedRecord, setSelectedRecord] = useStateUrlParams(
        "record",
        permissions.canAccessRecord301 ? RecordType.DataController : RecordType.DataProcessor
    );
    const queryPart = `?groupEntityId=${groupEntityId}&onlyUserSpecific=${onlyUserSpecific}&processingActivityIds=${processingActivityIds}&processingAreaIds=${processingAreaIds}&isExtended=${
        selectedRecord === RecordType.ExtentedDataController
    }`;

    const dataProcessorUrl = `/DataProcessorRecord${queryPart}`;
    const dataProcessorQuery = useDotLegalQuery<Array<DataProcessorRecordTableModel>>(dataProcessorUrl, {
        enabled: selectedRecord === RecordType.DataProcessor,
    });

    const dataControllerUrl = `/DataControllerRecord${queryPart}`;
    const dataControllerQuery = useDotLegalQuery<Array<DataControllerRecordModel>>(dataControllerUrl, {
        enabled: selectedRecord !== RecordType.DataProcessor,
    });

    const onGroupEntityChange = (groupEntity: string | null) => {
        setGroupEntityId(groupEntity);
    };

    const onExportClick = async () => {
        const exportData = getExportData();
        const date = new Date();
        const fileName = getExportFileName(selectedRecord === RecordType.DataProcessor, translateDate(date), date);
        setIsExporting(true);
        await downloadFile(exportData.url + queryPart, fileName, "POST", exportData.translations);
        setIsExporting(false);
        snackBar.show(fileName + " " + translateString("downloaded"));
    };

    const getExportData = () => {
        return selectedRecord === RecordType.DataProcessor
            ? { url: "/DataProcessorRecord/export", translations: getDataProcessorTranslations(translateString) }
            : { url: "/DataControllerRecord/export", translations: getDataControllerTranslations(translateString) };
    };

    const hasData =
        selectedRecord === RecordType.DataProcessor
            ? dataProcessorQuery.isLoading || dataProcessorQuery.data!.length > 0
            : dataControllerQuery.isLoading || dataControllerQuery.data!.length > 0;

    const disableExport = () => {
        if (!dataProtectionFeatures.recordsExport) return true;

        if (selectedRecord === RecordType.DataProcessor) return dataProcessorQuery.isLoading || dataProcessorQuery.data!.length === 0;
        else return dataControllerQuery.isLoading || dataControllerQuery.data!.length === 0;
    };

    const dataProcessorRows = filterDataProcessorRecord(dataProcessorQuery.data ?? [], searchString);

    function getRecordSelectOptions() {
        const recordSelectOptions = [];

        if (permissions.canAccessRecord301) {
            recordSelectOptions.push({ name: translateString("dataControllerRecord"), id: RecordType.DataController });
            recordSelectOptions.push({ name: translateString("dataControllerRecordExtended"), id: RecordType.ExtentedDataController });
        }

        if (permissions.canAccessRecord302) {
            recordSelectOptions.push({
                name: translateString("dataProcessorRecord"),
                id: RecordType.DataProcessor,
                hoverText: dataProtectionFeatures.recordDataProcessor === false ? translateString("upgradeDataProtectionPlan") : undefined,
                disabled: dataProtectionFeatures.recordDataProcessor === false ? true : false,
            });
        }

        return recordSelectOptions;
    }

    return {
        dataProcessorRows,
        dataProcessorRowsIsLoading: dataProcessorQuery.isLoading,
        dataControllerRows: dataControllerQuery.data ?? [],
        dataControllerRowsIsLoading: dataControllerQuery.isLoading,
        onGroupEntityChange,
        selectedGroupEntity: groupEntityId,
        processingActivityIds,
        setProcessingActivityIds,
        processingAreaIds,
        setProcessingAreaIds,
        onlyUserSpecific,
        setOnlyUserSpecific,
        onExportClick,
        isExporting,
        setSearchString,
        searchString,
        recordSelectOptions: getRecordSelectOptions(),
        selectedRecord,
        setSelectedRecord,
        isExportDisabled: disableExport(),
        hasData,
    };
}
